import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toImageUrl, handleImgError } from "@/lib/driveUrl";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Euro, Gauge, Plus } from "lucide-react";

const inputClass =
  "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 text-sm text-stone-800 focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-2 block text-xs font-bold uppercase text-stone-700";

function safeDate(dateStr) {
  try {
    return format(new Date(dateStr), "EEEE d MMMM yyyy", { locale: fr });
  } catch {
    return dateStr;
  }
}

function MetaChip({ icon: Icon, children, testId }) {
  if (!children) return null;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full bg-[#FAF6F0] px-3 py-1.5 text-xs font-bold text-stone-700 ring-1 ring-stone-200">
      <Icon className="h-3.5 w-3.5 text-[#B83227]" aria-hidden="true" />
      {children}
    </span>
  );
}

export default function Stages() {
  const [stages, setStages] = useState(null);
  const { settings } = useSite();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ stage: "", name: "", email: "", phone: "", level: "", message: "" });

  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data)).catch(() => setStages([]));
  }, []);

  const list = stages || [];
  const organizing = !!settings?.stages_organizing;
  const defaultChoice = "Être prévenu du prochain stage";
  const stageChoice = form.stage || defaultChoice;

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const chooseStage = (title) => {
    setForm((f) => ({ ...f, stage: title }));
    document.getElementById("inscription-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const chosen = list.find((s) => s.title === stageChoice);
    const details = [
      form.phone && `Téléphone : ${form.phone}`,
      form.level && `Niveau de pratique : ${form.level}`,
      form.message,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: `Inscription stage — ${stageChoice}`,
        prestation: `Inscription stage — ${stageChoice}`,
        event_info: chosen ? [chosen.date && safeDate(chosen.date), chosen.time, chosen.location].filter(Boolean).join(" · ") : "",
        message: [`Demande d'inscription : ${stageChoice}`, details].filter(Boolean).join("\n\n"),
      });
      setSent(true);
      toast.success("Votre demande d'inscription est bien envoyée !");
    } catch (err) {
      toast.error(formatApiError(err, "L'envoi a échoué. Réessayez dans un instant."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="stages-page">
      <Seo title="Stages de musique traditionnelle" description="Stages de cornemuse du Centre-France et de répertoire de bal : dates, lieux, tarifs, intervenants et formulaire d'inscription des Sonneurs de la Côte." path="/stages" />
      <section className="border-b border-stone-200 bg-[#1E1B18] py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">{siteText(settings, "page_stages_label", "Apprendre & transmettre")}</span>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight sm:text-6xl">{siteText(settings, "page_stages_title", "Stages")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base">
            {siteText(settings, "page_stages_intro", "Stages de musique traditionnelle : cornemuse du Centre-France et répertoire de bal, encadrés par des musiciens pédagogues.")}
          </p>
        </div>
      </section>

      <section className="bg-[#FAF6F0] py-16">
        <div className="mx-auto max-w-6xl space-y-8 px-4 sm:px-6">
          {/* Bandeau « prochain stage en cours d'organisation » (activé depuis l'admin) */}
          {organizing && (
            <div data-testid="stages-organizing-banner" className="overflow-hidden rounded-2xl border-2 border-dashed border-[#B83227]/50 bg-white shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3">
                {settings?.stages_organizing_photo && (
                  <div className="relative min-h-48 md:col-span-1" data-image-container>
                    <img src={toImageUrl(settings.stages_organizing_photo)} onError={handleImgError} alt="Prochain stage en préparation" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  </div>
                )}
                <div className={`p-6 sm:p-8 ${settings?.stages_organizing_photo ? "md:col-span-2" : "md:col-span-3"}`}>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#B83227]">À venir</span>
                  <h2 className="mt-1 font-display text-2xl font-bold uppercase text-[#1E1B18] sm:text-3xl">
                    {siteText(settings, "stages_organizing_title", "Prochain stage en cours d'organisation")}
                  </h2>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-stone-600">
                    {siteText(settings, "stages_organizing_text", "Les inscriptions ouvriront bientôt — laissez-nous vos coordonnées via le formulaire ci-dessous pour être prévenu en premier.")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Liste des stages */}
          {stages === null ? (
            <p className="text-sm text-stone-500">Chargement...</p>
          ) : list.length === 0 && !organizing ? (
            <p className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
              Aucun stage programmé pour le moment — laissez-nous vos coordonnées ci-dessous pour être prévenu du prochain.
            </p>
          ) : (
            list.map((s, i) => (
              <article key={s.id} data-testid={`stage-card-${i}`} className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {s.photo_url && (
                    <div className="relative min-h-52 md:col-span-1" data-image-container>
                      <img src={toImageUrl(s.photo_url)} onError={handleImgError} alt={`Stage ${s.title}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                  )}
                  <div className={`p-6 sm:p-8 ${s.photo_url ? "md:col-span-2" : "md:col-span-3"}`}>
                    <h2 className="font-display text-2xl font-bold uppercase text-[#1E1B18]">{s.title}</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <MetaChip icon={CalendarDays} testId={`stage-date-${i}`}>{s.date && safeDate(s.date)}</MetaChip>
                      <MetaChip icon={Clock} testId={`stage-time-${i}`}>{s.time}</MetaChip>
                      <MetaChip icon={MapPin} testId={`stage-location-${i}`}>{s.location}</MetaChip>
                      <MetaChip icon={Euro} testId={`stage-price-${i}`}>{s.price}</MetaChip>
                      <MetaChip icon={Gauge} testId={`stage-level-${i}`}>{s.level && `Niveau : ${s.level}`}</MetaChip>
                    </div>
                    {s.instruments && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {s.instruments.split(",").map((ins) => ins.trim()).filter(Boolean).map((ins) => (
                          <span key={ins} className="rounded bg-[#1E1B18] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">{ins}</span>
                        ))}
                      </div>
                    )}
                    {s.description && <p className="mt-4 max-w-2xl whitespace-pre-line text-sm leading-relaxed text-stone-600">{s.description}</p>}
                    {s.teachers?.length > 0 && (
                      <div className="mt-5 space-y-3 border-t border-stone-100 pt-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#B83227]">Intervenants</p>
                        {s.teachers.map((t, j) => (
                          <div key={j} data-testid={`stage-teacher-${i}-${j}`} className="flex items-start gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#B83227] font-display text-sm font-bold text-white">
                              {(t.name || "?").charAt(0).toUpperCase()}
                            </span>
                            <div>
                              <p className="text-sm font-bold text-[#1E1B18]">{t.name}</p>
                              {t.bio && <p className="text-xs leading-relaxed text-stone-600">{t.bio}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      data-testid={`stage-subscribe-${i}`}
                      onClick={() => chooseStage(s.title)}
                      className="mt-6 inline-block rounded bg-[#B83227] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E]"
                    >
                      S'inscrire à ce stage
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}

          {/* Formulaire d'inscription */}
          <div id="inscription-form" className="mx-auto max-w-3xl scroll-mt-28">
            <h2 className="mb-6 text-center font-display text-2xl font-bold uppercase text-[#1E1B18] sm:text-3xl">
              {siteText(settings, "stages_form_title", "Inscription & pré-inscription")}
            </h2>
            {sent ? (
              <div data-testid="stage-success-message" className="rounded-2xl border border-stone-300 bg-white p-10 text-center shadow-md">
                <p className="font-display text-2xl font-bold uppercase text-[#1E1B18]">Merci !</p>
                <p className="mt-3 text-sm text-stone-600">Votre demande est bien arrivée. Nous revenons vers vous très vite avec toutes les informations pratiques.</p>
                <button data-testid="stage-send-another-button" onClick={() => setSent(false)} className="mt-6 text-xs font-bold uppercase text-[#B83227] hover:underline">
                  Faire une autre demande
                </button>
              </div>
            ) : (
              <form data-testid="stage-inscription-form" onSubmit={submit} className="space-y-6 rounded-2xl border border-stone-300 bg-white p-8 shadow-md sm:p-10">
                <div>
                  <label htmlFor="stage-select" className={labelClass}>Stage concerné</label>
                  <select id="stage-select" data-testid="stage-select" className={inputClass} value={stageChoice} onChange={update("stage")}>
                    <option value={defaultChoice}>{defaultChoice}</option>
                    {list.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}{s.date ? ` — ${safeDate(s.date)}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="stage-name" className={labelClass}>Nom &amp; prénom *</label>
                    <input id="stage-name" data-testid="stage-name-input" type="text" className={inputClass} value={form.name} onChange={update("name")} required />
                  </div>
                  <div>
                    <label htmlFor="stage-email" className={labelClass}>E-mail *</label>
                    <input id="stage-email" data-testid="stage-email-input" type="email" className={inputClass} value={form.email} onChange={update("email")} required />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="stage-phone" className={labelClass}>Téléphone (facultatif)</label>
                    <input id="stage-phone" data-testid="stage-phone-input" type="tel" className={inputClass} value={form.phone} onChange={update("phone")} />
                  </div>
                  <div>
                    <label htmlFor="stage-level" className={labelClass}>Votre niveau de pratique</label>
                    <input id="stage-level" data-testid="stage-level-input" type="text" className={inputClass} placeholder="Ex : débutant, 2 ans de cabrette..." value={form.level} onChange={update("level")} />
                  </div>
                </div>
                <div>
                  <label htmlFor="stage-message" className={labelClass}>Message (facultatif)</label>
                  <textarea id="stage-message" data-testid="stage-message-input" rows="4" className={inputClass} placeholder="Une question, un instrument prêté à prévoir..." value={form.message} onChange={update("message")} />
                </div>
                <div className="flex flex-col items-center justify-between gap-4 border-t border-stone-200 pt-4 sm:flex-row">
                  <span className="text-xs text-stone-500">Vos coordonnées servent uniquement à vous recontacter au sujet du stage.</span>
                  <button
                    type="submit"
                    data-testid="stage-submit-button"
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded bg-[#B83227] px-8 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E] disabled:opacity-60 sm:w-auto"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {sending ? "Envoi en cours..." : "Envoyer mon inscription"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
